import { buildId } from "@faire/slate/lib/idUtils";
import { componentGroupCls } from "@faire/slate/lib/tracking";
import { IAccordionGroupProps } from "@faire/slate/private/accordion/types";
import type { IText } from "@faire/slate/private/shared/types";
import React from "react";

import { AccordionItem } from "./AccordionItem";

type IProps = {
  /**
   * The props of all the accordion items in the accordion group.
   */
  items: Array<
    Omit<React.ComponentProps<typeof AccordionItem>, "expanded" | "onToggle">
  >;

  /**
   * Optional footer text to display at the bottom of the accordion group.
   */
  footer?: IText;

  /**
   * **web**
   * The index of the AccordionItem that is initially expanded.
   */
  initiallyExpandedIndex?: number;

  /**
   * **web**
   * Callback function that is triggered when an AccordionItem is clicked
   *
   * @param index - The index of the AccordionItem that was clicked
   * @param e - The toggle event object, see https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/toggle_event
   */
  onToggle?: (index: number, e: React.ToggleEvent<HTMLDetailsElement>) => void;

  // DO NOT ADD any new prop without aligning with Slate team first
} & IAccordionGroupProps;

/**
 * Accordions help users understand detailed information via an expandable structure.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Accordion-2242efb5c25a80d68921e5c3b806f984 | Slate/AccordionGroup}
 */
export const AccordionGroup: React.FC<IProps> = ({
  footer,
  items,
  initiallyExpandedIndex,
  onToggle,
  ...props
}) => (
  <section
    {...props}
    className={componentGroupCls("fs-accordion-group")}
    data-component-name="Slate/AccordionGroup"
  >
    {items.map((item, index) => (
      <AccordionItem
        key={buildId(`accordion-item-${index}`, item.id, item.label)}
        {...item}
        expanded={initiallyExpandedIndex === index}
        onToggle={(e) => onToggle?.(index, e)}
      />
    ))}
    {footer ? <p className="fs-accordion-group__footer">{footer}</p> : null}
  </section>
);
